import { ColumnVisibleEvent, GridApi } from 'ag-grid-community';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import {
  allColIds,
  getPinnedYearColId,
  parsePinnedYearIndex,
  pinnedYearIndices,
} from './usePinnedYearColDefs';

export const defaultPinnedYearIndices = [1, 2];

const selectedYearsState = atom<Set<number>>(new Set([...defaultPinnedYearIndices]));

export const financialsGridApiState = atom<GridApi | null>(null);

export function useVisibleColumns() {
  const [selectedYears, setSelectedYears] = useAtom(selectedYearsState);
  const gridApi = useAtomValue(financialsGridApiState);

  const onHideSingleColumn = useCallback(
    (colId: string) => {
      gridApi?.setColumnsVisible([colId], false);
    },
    [gridApi]
  );

  const onChangeSelectedYears = useCallback(
    (newValue: number[]) => {
      if (!gridApi) {
        return;
      }
      const pinned = new Set(newValue);

      const visibleIds = [] as string[];
      const hiddenIds = [] as string[];
      pinnedYearIndices.forEach((year) => {
        if (pinned.has(year)) {
          visibleIds.push(getPinnedYearColId(year));
        } else {
          hiddenIds.push(getPinnedYearColId(year));
        }
      });
      gridApi.setColumnsVisible(visibleIds, true);
      gridApi.setColumnsVisible(hiddenIds, false);
    },
    [gridApi]
  );

  const onColumnVisible = useCallback(
    (e: ColumnVisibleEvent) => {
      const affectedColumns = e.columns?.map((col) => col.getColId()) || [];
      const hasPinnedYear = affectedColumns.some((col) => allColIds.has(col));
      if (!hasPinnedYear) {
        return;
      }
      setSelectedYears(getSelectedYears(e.api));
    },
    [setSelectedYears]
  );

  return {
    onChangeSelectedYears,
    onColumnVisible,
    onHideSingleColumn,
    selectedYears,
  };
}

function getSelectedYears(api?: GridApi) {
  const result = new Set<number>();
  api?.getColumnState().forEach((col) => {
    if (allColIds.has(col.colId) && !col.hide) {
      result.add(parsePinnedYearIndex(col.colId));
    }
  });

  return result;
}
